export const getItemNameKey = (itemName) => {
  if (!itemName) return '';
  if (typeof itemName === 'string') return itemName.trim();
  return String(itemName.label || itemName.value || '').trim();
};

export const getItemTypeKey = (item) => item.itemType ?? item.type ?? '';

const normalizeColor = (color) => (color || '').trim().toLowerCase();

export const mergeColors = (existingColor, newColor) => {
  if (!newColor) return existingColor || '';
  if (!existingColor) return newColor.trim();
  const colors = existingColor.split(',').map((c) => c.trim()).filter(Boolean);
  if (colors.some((c) => normalizeColor(c) === normalizeColor(newColor))) {
    return existingColor;
  }
  return [...colors, newColor.trim()].join(', ');
};

export const appendColorToDescription = (description, color) => {
  if (!color) return description || '';
  const desc = description || '';
  const plainText = desc.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  if (!plainText) return color;

  const parts = plainText.split(',').map((s) => s.trim()).filter(Boolean);
  if (parts.some((p) => normalizeColor(p) === normalizeColor(color))) {
    return desc;
  }

  if (desc.includes('<')) {
    if (desc.includes('</p>')) {
      return desc.replace(/<\/p>\s*$/, `, ${color.trim()}</p>`);
    }
    return `${desc}, ${color.trim()}`;
  }
  return `${plainText}, ${color.trim()}`;
};

export const calculateItemPrice = (qty, unitPrice, discount = 0, discountType = 'percentage') => {
  const base = Number(qty || 0) * Math.round(Number(unitPrice || 0));
  const disc = Number(discount || 0);
  const discAmt = discountType === 'fixed' ? disc : (base * disc) / 100;
  return Math.round(Math.max(0, base - discAmt));
};

export const findMergeableItemIndex = (items, { itemName, itemType, uom, unitPrice, discount = 0, discount_type = 'percentage' }) => {
  const nameKey = getItemNameKey(itemName);
  const price = parseFloat(unitPrice);
  const disc = parseFloat(discount || 0);

  return items.findIndex(
    (item) =>
      getItemNameKey(item.itemName) === nameKey &&
      getItemTypeKey(item) === itemType &&
      item.uom === uom &&
      parseFloat(item.unitPrice) === price &&
      parseFloat(item.discount || 0) === disc &&
      (disc === 0 || (item.discount_type || item.discountType || 'percentage') === (discount_type || 'percentage'))
  );
};

export const parseColorString = (colorString, totalQty) => {
  if (!colorString) return {};
  const map = {};
  const parts = String(colorString).split(',').filter(Boolean);
  
  let hasUnquantified = false;
  
  for (const part of parts) {
    const trimmed = part.trim();
    const match = trimmed.match(/^(.*?)\s*\((\d+(?:\.\d+)?)(?:qty)?\)$/);
    if (match) {
      map[match[1].trim()] = parseFloat(match[2]);
    } else {
      map[trimmed] = 0;
      hasUnquantified = true;
    }
  }
  
  if (parts.length === 1 && hasUnquantified) {
    map[parts[0].trim()] = parseFloat(totalQty) || 0;
  }
  
  return map;
};

export const buildMergedItem = (existingItem, { qty, color, unitPrice }) => {
  const mergedQty = parseFloat(existingItem.qty) + qty;
  const newColor = (color || '').trim();
  const existingColor = existingItem.color || '';
  
  const colorMap = parseColorString(existingColor, existingItem.qty);
  
  if (newColor) {
    colorMap[newColor] = (colorMap[newColor] || 0) + qty;
  }
  
  const updatedColor = Object.entries(colorMap)
    .map(([c, q]) => `${c} (${q}qty)`)
    .join(', ');

  let updatedDescription = existingItem.description || '';
  if (newColor && existingColor && !existingColor.includes(newColor)) {
    updatedDescription = appendColorToDescription(updatedDescription, newColor);
  }

  return {
    ...existingItem,
    qty: mergedQty,
    color: updatedColor,
    description: updatedDescription,
    price: (mergedQty * parseFloat(unitPrice)).toFixed(2),
  };
};
